import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getSocialMediaText } from '@anytag/shared-utils';
import { useSelectItem, useToggleState } from '@anytag/shared-hooks';
import { Icon, Anchor, Form, BaseModal } from '@anytag/shared-atoms';
import { CampaignType, JoinedCampaign, SocialAccountType, useFindJoinedCampaignsQuery } from '@anytag/graphql';
import { InfluencerFormValues } from '../../schema-types';
import {
  SocialMediaInputFieldAction,
  SocialMediaInputFieldsAction,
  SocialMediaInputFieldsActionProps
} from './SocialMediaInputFieldsAction';

export const SocialAccountsSection = () => {
  const { t } = useTranslation();
  const warningModal = useToggleState();
  const confirmModal = useToggleState();
  const { watch } = useFormContext<InfluencerFormValues>();

  const [influencerId, facebookAccountValue, douyinAccountsValues, xhsAccountsValues] = watch([
    'id',
    'facebookAccount',
    'douyinAccounts',
    'xhsAccounts'
  ]);

  const { selectedItem, setSelectedItem, resetSelectedItem } = useSelectItem<{
    callback: () => void;
    socialAccountId?: number;
    socialMedia: SocialAccountType;
    joinedCampaigns: JoinedCampaign[];
  }>();
  const { data } = useFindJoinedCampaignsQuery({
    skip: !influencerId,
    fetchPolicy: 'cache-first',
    variables: { influencerId: Number(influencerId) }
  });

  const joinedCampaigns = data?.findJoinedCampaigns || [];
  const socialMediaText = getSocialMediaText(selectedItem?.socialMedia);

  const getSocialMediaHelp = (username: string, link: string) => (
    <div>
      {`${t('Tooltip.eg')} ${link}`}
      <br />
      {`=> ${t('Tooltip.username')} : '${username}'`}
    </div>
  );

  const handleDeleteAccount: SocialMediaInputFieldsActionProps['onDelete'] = ({
    callback,
    socialMedia,
    account: { socialAccountId }
  }) => {
    const accountJoinedCampaigns = joinedCampaigns.filter(
      (c) => c.socialAccountId === socialAccountId && c.socialAccountType === socialMedia
    );

    if (socialAccountId) {
      setSelectedItem({
        callback,
        socialMedia,
        socialAccountId,
        joinedCampaigns: accountJoinedCampaigns
      });

      if (accountJoinedCampaigns.length) {
        warningModal.open();
      } else {
        confirmModal.open();
      }
    } else {
      callback();
    }
  };

  return (
    <>
      <Form.SectionTitle className="mb-0" label={t('Social Account')} />

      <SocialMediaInputFieldAction
        placeholder="zuck"
        influencerId={influencerId}
        account={facebookAccountValue}
        name="facebookAccount.username"
        help={t('Tooltip.Extension Help')}
        title={t('TextForm.Facebook Username')}
        socialMedia={SocialAccountType.FACEBOOK}
        disabled={!facebookAccountValue.isAdding}
      />

      <SocialMediaInputFieldsAction
        name="facebookPages"
        placeholder="AnyTag"
        influencerId={influencerId}
        onDelete={handleDeleteAccount}
        help={t('Tooltip.Extension Help')}
        title={t('TextForm.Facebook Page Name')}
        socialMedia={SocialAccountType.FACEBOOK_PAGE}
        addButtonProps={{ title: t('Button.add page') }}
      />

      <SocialMediaInputFieldsAction
        influencerId={influencerId}
        name="instagramAccounts"
        placeholder="anymindgroup"
        onDelete={handleDeleteAccount}
        title={t('TextForm.Instagram Username')}
        socialMedia={SocialAccountType.INSTAGRAM}
        help={getSocialMediaHelp('anymindgroup', 'https://www.instagram.com/anymindgroup/')}
      />

      <SocialMediaInputFieldsAction
        name="twitterAccounts"
        placeholder="AnyMindGroup"
        influencerId={influencerId}
        onDelete={handleDeleteAccount}
        title={t('TextForm.X(Twitter) Username')}
        socialMedia={SocialAccountType.TWITTER}
        help={getSocialMediaHelp('AnyMindGroup', 'https://twitter.com/AnyMindGroup')}
      />

      <SocialMediaInputFieldsAction
        name="youtubeAccounts"
        influencerId={influencerId}
        onDelete={handleDeleteAccount}
        title={t('TextForm.YouTube URL')}
        socialMedia={SocialAccountType.YOUTUBE}
        addButtonProps={{ title: t('Button.add channel') }}
        placeholder="https://www.youtube.com/c/AnyMindGroup1"
        help={getSocialMediaHelp('AnyMindGroup1', 'https://www.youtube.com/c/AnyMindGroup1')}
      />

      <SocialMediaInputFieldsAction
        name="tiktokAccounts"
        influencerId={influencerId}
        onDelete={handleDeleteAccount}
        placeholder="anymind_creatorgrowth"
        title={t('TextForm.TikTok Username')}
        socialMedia={SocialAccountType.TIKTOK}
        help={getSocialMediaHelp('anymind_creatorgrowth', 'https://www.tiktok.com/@anymind_creatorgrowth')}
      />

      {douyinAccountsValues?.length ? (
        <SocialMediaInputFieldsAction
          name="douyinAccounts"
          influencerId={influencerId}
          title={t('TextForm.Douyin Username')}
          socialMedia={SocialAccountType.DOUYIN}
        />
      ) : null}

      <SocialMediaInputFieldsAction
        name="threadsAccounts"
        placeholder="anymindgroup"
        influencerId={influencerId}
        onDelete={handleDeleteAccount}
        title={t('TextForm.Threads Username')}
        socialMedia={SocialAccountType.THREADS}
        help={getSocialMediaHelp('anymindgroup', 'https://www.threads.net/@anymindgroup/')}
      />

      {xhsAccountsValues?.length ? (
        <SocialMediaInputFieldsAction
          name="xhsAccounts"
          influencerId={influencerId}
          title={t('TextForm.Xiaohongshu Username')}
          socialMedia={SocialAccountType.XHS}
        />
      ) : null}

      <BaseModal
        open={confirmModal.status}
        onClose={confirmModal.close}
        title={
          <>
            <Icon size={20} icon="warning" variant="red" className="mr-2" />
            {t('Dialog.Delete social account', { socialMedia: socialMediaText })}
          </>
        }
        description={
          <>
            {t('deleteSocialAccountConfirmText01', { socialMedia: socialMediaText })}
            <br />
            {t('deleteSocialAccountConfirmText02')}
          </>
        }
        footer={{
          cancelButton: {
            onClick: () => {
              confirmModal.close();
              resetSelectedItem();
            }
          },
          submitButton: {
            color: 'danger',
            onClick: () => {
              selectedItem?.callback();
              confirmModal.close();
              resetSelectedItem();
            }
          }
        }}
      />

      <BaseModal
        open={warningModal.status}
        onClose={warningModal.close}
        title={
          <>
            <Icon size={20} icon="warning" variant="yellow" className="mr-2" />
            {t('Dialog.This account cannot be deleted')}
          </>
        }
        description={t('deleteSocialAccountWhileJoinedCampaignWarningText')}
        footer={{
          cancelButton: {
            onClick: () => {
              warningModal.close();
              resetSelectedItem();
            }
          }
        }}
      >
        {selectedItem?.joinedCampaigns
          ?.slice(0, 5)
          .map(({ id, type, title }) => (
            <Anchor
              key={`joined-campaign-${id}`}
              label={title}
              variant="blue"
              target="_blank"
              to={type === CampaignType.ENGAGEMENT ? '/engagement/$id/proposal' : '/marketplace/$id'}
              params={{ id: id.toString() }}
            />
          ))}
      </BaseModal>
    </>
  );
};
